<template>
  <div class="code-face">
    <div class="code-face-top">
      <!-- Sits just above the code, outside the row the centre is measured from. -->
      <div v-if="api.passerbyCode" class="sheet-label">
        {{ localize('handshake.passerby.hint') }}
      </div>
      <!-- Me, and the seat the code fills. -->
      <HandshakePairFaces
        v-else
        class="code-faces"
        open-seat
        :seat-label="localize('handshake.code.typeTheirs')"
        @seat="askTheirCode"
      />
    </div>

    <!--
      The middle of the board is the code alone. The heading stands above it,
      and a made passerby code takes the same slot, so nothing moves.
    -->
    <div class="code-face-middle">
      <div class="code-centre">
        <div v-if="api.passerbyCode" class="code-slot">
          <div class="code-value">{{ api.passerbyCode }}</div>
        </div>

        <div v-else class="code-pair" :class="{ 'is-waiting': api.waitingForUsername }">
          <div class="code-slot">
            <div class="code-value" :class="{ 'is-pending': !api.code, 'is-expired': api.code && api.expiresIn <= 0 }">
              {{ api.code || MEMBER_CODE_STANDIN }}
            </div>
            <div v-if="!api.code" class="code-loading skeleton" />

            <!-- Hangs under the code without taking a row, so the code keeps the middle. -->
            <div class="timer-slot">
              <span v-if="api.expiresIn > 0">{{ api.expiresIn }}s</span>
              <span v-else-if="api.code" class="code-expired" @click="api.refresh()">
                {{ localize('handshake.code.expired') }}
              </span>
            </div>
          </div>

          <span class="code-mark">
            <GamIcon :name="GamIconName.HANDSHAKE_EMPTY" size="5cqmin" is-current />
          </span>

          <div v-if="api.waitingForUsername" class="waiting-message">
            {{ localizeWithValues('handshake.code.waiting', [api.waitingForUsername]) }}
          </div>

          <input
            v-else
            ref="codeInput"
            v-model="api.inputCode"
            :placeholder="askingTheirCode ? localize('handshake.code.typeTheirs') : undefined"
            type="text"
            :maxlength="PASSERBY_CODE_LENGTH"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            class="code-input"
            :class="{ 'is-error': api.inputError, 'is-long': api.isPasserbyEntry }"
            :disabled="api.resolving"
            @input="api.onInput()"
          />
        </div>
      </div>
    </div>

    <div class="code-face-bottom">
      <button
        v-if="api.showSubmit && api.codeReady"
        type="button"
        class="submit-code"
        :disabled="api.resolving"
        @click="api.onInput()"
      >
        {{ localize('handshake.code.submit') }}
      </button>
      <button
        v-else-if="api.showSubmit"
        type="button"
        class="mode-cta"
        :disabled="api.creating"
        @click="api.createPasserby()"
      >
        <GamIcon :name="GamIconName.PLUS" size="12px" is-current />
        {{ localize('handshake.passerby.add') }}
      </button>
    </div>

    <!-- On the bottom edge, outside the rows, so the code keeps the middle of the board. -->
    <div v-if="api.passerbyCode" class="code-done">
      <button type="button" class="code-round" :aria-label="localize('handshake.passerby.close')" @click="emit('done')">
        <GamIcon :name="GamIconName.X_CLOSE" size="22px" is-current />
      </button>
      <!-- Beside the X, so the X keeps the middle. -->
      <div class="code-done-beside"><slot name="beside-done" /></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { localize, localizeWithValues } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import HandshakePairFaces from '@/views/main/handshakes/HandshakePairFaces.vue';
import { useProvidedHandshakeCode } from '@/views/main/handshakes/use-handshake-code';
import { MEMBER_CODE_STANDIN, PASSERBY_CODE_LENGTH } from '@/views/main/handshakes/utils/handshake-code';
import { ref } from 'vue';

const emit = defineEmits<{
  done: [];
}>();

const api = useProvidedHandshakeCode();

const codeInput = ref<HTMLInputElement>();
/** The empty seat was tapped, so the line says whose code goes on it. */
const askingTheirCode = ref(false);

const askTheirCode = () => {
  askingTheirCode.value = true;
  codeInput.value?.focus();
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

/*
 * The same three rows the sheet uses, so this face keeps the middle of the
 * board when it takes the sheet over. It is not a dialogue of its own.
 */
.code-face {
  position: relative;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto minmax(0, 1fr);
  justify-items: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 0;
  text-align: center;
  container-type: size;
  container-name: handshake-sheet;
}

.code-face-top,
.code-face-bottom {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 0;
}

/* The faces stand as tall as the code, so neither outweighs the other at any size. */
.code-face-top,
.code-face-middle {
  --code-size: min(48px, 18cqmin);
}

.code-face-top {
  justify-content: flex-start;
}

.code-face-bottom {
  justify-content: flex-end;
}

.code-done {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

.code-done-beside {
  position: absolute;
  top: 50%;
  left: calc(100% + var(--spacing-md));
  transform: translateY(-50%);
}

.code-round {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  padding: 0;
  border: 1px solid var(--color-white-10);
  border-radius: 50%;
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--color-white-70);
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    color: var(--color-white);
    border-color: var(--color-white-20);
  }

  @include press;
}

.code-face-middle {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.sheet-label {
  margin-top: auto;
  margin-bottom: var(--spacing-md);
  font-size: 14px;
  font-weight: 600;
  color: var(--color-white);
  text-transform: uppercase;
  letter-spacing: 2px;
  text-indent: 2px;
  line-height: 20px;
}

/* The faces fill the top, above the code. */
.code-faces {
  --pair-face: var(--code-size);
  margin-block: auto;
}

.code-centre {
  --code-slot-height: min(56px, 22cqmin);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
}

.code-pair {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
}

.code-mark {
  display: none;
  color: var(--color-white);
}

.code-slot {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--code-slot-height);
}

.code-value {
  font-size: var(--code-size);
  font-weight: 700;
  letter-spacing: 8px;
  text-indent: 8px;
  text-transform: lowercase;
  color: var(--color-pink);
  font-family: 'Hanken Grotesk', monospace;
  user-select: all;
  line-height: 1.1;
}

.code-value.is-long {
  font-size: min(36px, 12cqmin);
  letter-spacing: 6px;
  text-indent: 6px;
}

.code-value.is-pending {
  visibility: hidden;
}

.code-value.is-expired {
  color: var(--color-white-50);
}

.code-loading {
  position: absolute;
  inset: 0;
  width: auto;
  height: auto;
  border-radius: var(--radius-normal);
}

.timer-slot {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  height: 20px;
  white-space: nowrap;
  font-size: 14px;
  letter-spacing: normal;
  text-indent: 0;
  color: var(--color-white-50);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Stacked, the input sits right under the code, so the timer needs its own room. */
.code-pair .code-slot {
  margin-bottom: 20px;
}

.code-expired {
  color: var(--color-pink);
  cursor: pointer;
  text-decoration: underline;
}

.code-input {
  --code-chars: 4;
  --code-tracking: 8px;
  font-size: var(--code-size);
  font-weight: 700;
  letter-spacing: var(--code-tracking);
  text-indent: var(--code-tracking);
  text-align: center;
  color: var(--color-pink);
  font-family: 'Hanken Grotesk', monospace;
  background: transparent;
  border: none;
  border-bottom: 2px solid var(--color-pink);
  outline: none;
  width: calc(var(--code-chars) * 1ch + var(--code-chars) * var(--code-tracking));
  height: min(52px, 17cqmin);
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  caret-color: var(--color-pink);

  &:disabled {
    opacity: 0.6;
  }

  /* A label on the line, not a code: small, so it fits the width of four characters. */
  &::placeholder {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: normal;
    text-indent: 0;
    color: var(--color-white-50);
    opacity: 1;
  }

  &.is-error {
    color: #e53935;
    border-bottom-color: #e53935;
  }

  &.is-long {
    --code-chars: 6;
    --code-tracking: 6px;
    font-size: min(36px, 12cqmin);
  }
}

@container handshake-sheet (min-width: 265px) {
  .code-face-top,
  .code-pair {
    --code-size: 14cqmin;
  }

  .code-pair {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: end;
    column-gap: 2.5cqmin;
  }

  .code-pair.is-waiting {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-md);

    .code-mark {
      display: none;
    }
  }

  .code-pair .code-slot {
    justify-self: end;
  }

  .code-pair .code-mark {
    display: flex;
    align-self: center;
    justify-self: center;
  }

  .code-pair:not(.is-waiting) .code-slot {
    margin-bottom: 0;
  }

  .code-pair .code-value {
    letter-spacing: 0.17em;
    text-indent: 0.17em;
  }

  .code-pair .code-input {
    --code-tracking: 0.17em;
    justify-self: start;
    align-self: flex-end;

    &.is-long {
      --code-tracking: 0.18em;
      font-size: 8.5cqmin;
    }
  }
}

.submit-code {
  padding: 8px 18px;
  border: 1px solid var(--color-white-30);
  border-radius: 999px;
  background: none;
  color: var(--color-white);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;

  &:not(:disabled) {
    @include press;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
    pointer-events: none;
  }
}

.waiting-message {
  padding: 0 var(--spacing-l);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
  color: var(--color-white-70);
}

.mode-cta {
  display: flex;
  align-items: center;
  gap: var(--spacing-s);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  white-space: nowrap;
  color: var(--color-white-70);
  background: none;
  border: none;
  padding: var(--spacing-xs) var(--spacing-md);
  cursor: pointer;
  touch-action: manipulation;

  @include press;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}
</style>
